import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Button,
  TextField,
  Divider,
} from '@mui/material';
import { MyLocation, Clear as ClearIcon } from '@mui/icons-material';
import { RootState } from '../../store/store';
import {
  startHomeBasePlacement,
  setHomeBase,
  clearHomeBase,
  setReturnToHome,
} from '../../store/slices/missionPlannerSlice';
import { formatLatLng } from '../../utils/formatters';

const inRange = (v: number, max: number) =>
  Number.isFinite(v) && v >= -max && v <= max;

/**
 * Return-to-Home toggle + home-base selector (issue #296). The home base can
 * be set by clicking the map or by typing GPS coordinates; when unset the RTH
 * leg falls back to the mission start point.
 */
export const HomeBaseControls: React.FC = () => {
  const dispatch = useDispatch();
  const homeBase = useSelector(
    (state: RootState) => state.missionPlanner.draft.homeBase
  );
  const returnToHome = useSelector(
    (state: RootState) => state.missionPlanner.returnToHome
  );
  const waypointCount = useSelector(
    (state: RootState) => state.missionPlanner.draft.waypoints.length
  );

  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [inputError, setInputError] = useState<string | null>(null);

  const applyGps = () => {
    const latNum = Number(lat);
    const lngNum = Number(lng);
    if (!inRange(latNum, 90) || !inRange(lngNum, 180)) {
      setInputError('Enter a valid latitude (−90…90) and longitude (−180…180).');
      return;
    }
    setInputError(null);
    dispatch(setHomeBase({ lat: latNum, lng: lngNum }));
    setLat('');
    setLng('');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="subtitle2" component="h3">
        Home &amp; return
      </Typography>

      <FormControlLabel
        control={
          <Switch
            checked={returnToHome}
            onChange={(e) => dispatch(setReturnToHome(e.target.checked))}
            disabled={waypointCount === 0}
            inputProps={{ 'aria-label': 'Return to Home' }}
          />
        }
        label="Return to home — close the loop"
      />

      {/* Where the return leg goes: a custom home, or the default start point. */}
      <Typography variant="body2" color="text.secondary">
        {homeBase ? (
          <>Returns to your home base: {formatLatLng(homeBase.lat, homeBase.lng)}</>
        ) : (
          <>
            Closes the loop — returns to the start (waypoint 1). Set a custom
            home below to change where it lands.
          </>
        )}
      </Typography>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<MyLocation />}
          onClick={() => dispatch(startHomeBasePlacement())}
        >
          Set on map
        </Button>
        {homeBase && (
          <Button
            size="small"
            variant="text"
            startIcon={<ClearIcon />}
            onClick={() => dispatch(clearHomeBase())}
          >
            Reset
          </Button>
        )}
      </Box>

      {/* GPS coordinate entry. */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <TextField
          size="small"
          label="Latitude"
          value={lat}
          onChange={(e) => setLat(e.target.value)}
          error={Boolean(inputError)}
          inputProps={{ inputMode: 'decimal', 'aria-label': 'Home base latitude' }}
        />
        <TextField
          size="small"
          label="Longitude"
          value={lng}
          onChange={(e) => setLng(e.target.value)}
          error={Boolean(inputError)}
          inputProps={{ inputMode: 'decimal', 'aria-label': 'Home base longitude' }}
        />
        <Button size="small" variant="contained" onClick={applyGps} sx={{ mt: 0.5 }}>
          Set
        </Button>
      </Box>
      {inputError && (
        <Typography variant="caption" color="error" role="alert">
          {inputError}
        </Typography>
      )}

      <Divider />
    </Box>
  );
};
